<script setup lang="ts">
import { BaseDrawer } from "@voltalis/ui-kit/components";
import { useTemplateRef } from "vue";

import { IEditSiteDetailsDrawerExpose } from "@/components/tools/site-details/edit-site-details/edit-site-details-drawer.types";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import {
  TEditSiteConsumptionInfosBody,
  TSiteConsumptionInfos,
} from "@/data/api/kili/consumption/types/consumption-infos.type";
import { useTypedIntl } from "@/locales/typed-intl";

import EditSiteDetailsForm from "@/components/tools/site-details/edit-site-details/edit-site-details-form.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  siteName?: string;
  siteConsumptionInfos?: TSiteConsumptionInfos;
  isLoading?: boolean;
}

interface IEmits {
  (e: "hide"): void;
  (e: "submit", payload: TEditSiteConsumptionInfosBody): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const visible = defineModel<boolean>("visible", {
  required: true,
});

const { t, k } = useTypedIntl();

const drawer = useTemplateRef("drawer");
const editSiteDetailsForm = useTemplateRef("editSiteDetailsForm");

async function handleSubmit() {
  const payload = await editSiteDetailsForm.value?.submit();

  if (!payload) return;

  emits("submit", payload);
}

function closeDrawer() {
  drawer.value?.handleClose();
}

defineExpose<IEditSiteDetailsDrawerExpose>({
  closeDrawer,
});
</script>

<template>
  <BaseDrawer
    ref="drawer"
    v-model="visible"
    header-class="bg-blue-100/50 border-none"
    :data-test-id="DATA_TEST_IDS.components.editSiteDetailsDrawer.component"
  >
    <template #title>
      <div class="flex items-center gap-2 text-brand">
        <i class="pi pi-pencil text-sm" />
        <div class="flex flex-col gap-0">
          <span
            class="font-semibold leading-tight"
            :data-test-id="DATA_TEST_IDS.components.editSiteDetailsDrawer.title"
          >
            {{ t(k.tools.siteManagement.siteDetails.edition.editionMode) }}
          </span>
          <span
            class="text-sm leading-tight"
            :data-test-id="DATA_TEST_IDS.components.editSiteDetailsDrawer.subtitle"
          >
            {{ t(k.tools.siteManagement.siteDetails.edition.changesNotSavedYet) }}
          </span>
        </div>
      </div>
    </template>

    <div class="flex flex-col justify-between h-full overflow-auto">
      <div class="flex flex-col gap-2 py-8 overflow-y-auto px-6">
        <EditSiteDetailsForm
          ref="editSiteDetailsForm"
          :site-name="props.siteName"
          :site-consumption-infos="props.siteConsumptionInfos"
          :is-loading="props.isLoading"
        />
      </div>

      <div class="flex flex-col gap-3 w-full px-6 pb-6">
        <HospitalityButton
          :data-test-id="DATA_TEST_IDS.components.editSiteDetailsDrawer.saveButton"
          :label="t(k.global.misc.save)"
          :loading="props.isLoading"
          size="sm"
          @click="handleSubmit"
        />
        <HospitalityButton
          :data-test-id="DATA_TEST_IDS.components.editSiteDetailsDrawer.cancelButton"
          :label="t(k.global.misc.close)"
          severity="secondary"
          size="sm"
          @click="drawer?.handleClose()"
        />
      </div>
    </div>
  </BaseDrawer>
</template>
